import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import { StorageValues } from 'src/app/modules/shared/entities/storage-values.enum';

export type SelectedAccountManager = Pick<AccountManager, 'employeeId' | 'name'>;

const AM_FILTERED_LIST_KEYS = [
  StorageValues.AccountsKey, StorageValues.QuoteKey, StorageValues.OrderKey, StorageValues.InvoiceKey, StorageValues.LnOrderKey
];
const LEADS_LIST_KEYS = [StorageValues.LeadsKey, StorageValues.LeadsHistoryKey];
const PROJECTS_LIST_KEYS = [StorageValues.ProjectsKey, StorageValues.ProjectsHistoryKey];
const DEFAULT_ACCOUNTS_FILTERS = {
  filterByInterval: 'all',
  accounts: true,
  delivery: true,
  leads: true,
  myCustomers: false
};
// Default form values the lists cache along with their filters, so they don't count as active filters
const INACTIVE_ACCOUNTS_FILTERS = { ...DEFAULT_ACCOUNTS_FILTERS, searchBy: 1, lnStatus: 0, stateId: 0, country: 0 };
const INACTIVE_TRANSACTION_FILTERS = { quotesShowFor: 1, ordersShowFor: 1, lnOrderStatus: 0 };
const ACCOUNT_MANAGER_FILTERS = {
  accountManager: 'accountManager',
  leadOwner: 'leadOwnerId',
  projectOwner: 'projectOwnerId'
} as const;
type AccountManagerFilter = typeof ACCOUNT_MANAGER_FILTERS[keyof typeof ACCOUNT_MANAGER_FILTERS];
const DISPLAYED_SEARCH_VALUE_SEPARATOR = '; ';

@Injectable({
  providedIn: 'root'
})
export class SelectedAccountManagerService {
  private readonly selectedSubject = new BehaviorSubject<SelectedAccountManager | null>(this.readFromStorage());
  readonly selected$ = this.selectedSubject.asObservable();

  get selected(): SelectedAccountManager | null {
    return this.selectedSubject.value;
  }

  /**
   * Selects an Account Manager everywhere; a missing employeeId clears the selection
   * @param accountManager account manager from the account managers list (id is needed by the Leads/Projects filters)
   */
  select(accountManager: Partial<AccountManager> | null) {
    if (!accountManager?.employeeId) {
      this.clear();
      return;
    }

    if (accountManager.employeeId === this.selected?.employeeId) {
      return;
    }

    const selected: SelectedAccountManager = { employeeId: accountManager.employeeId, name: accountManager.name ?? '' };
    sessionStorage.setItem(StorageValues.SelectedAMId, selected.employeeId);
    sessionStorage.setItem(StorageValues.SelectedAMName, selected.name);
    if (accountManager.id) {
      sessionStorage.setItem(StorageValues.SelectedAMOwnerId, accountManager.id);
    } else {
      sessionStorage.removeItem(StorageValues.SelectedAMOwnerId);
    }
    this.resetCachedFilters(selected, accountManager.id);
    this.selectedSubject.next(selected);
  }

  /**
   * Clears the selected Account Manager everywhere, keeping the other cached filters of every list
   */
  clear() {
    const selected = this.selected;
    if (!selected) {
      return;
    }

    AM_FILTERED_LIST_KEYS.forEach(key => this.removeCachedFilter(key, ACCOUNT_MANAGER_FILTERS.accountManager,
      accountManager => this.getEmployeeId(accountManager) === selected.employeeId));

    const ownerId = sessionStorage.getItem(StorageValues.SelectedAMOwnerId);
    if (ownerId) {
      LEADS_LIST_KEYS.forEach(key =>
        this.removeCachedFilter(key, ACCOUNT_MANAGER_FILTERS.leadOwner, leadOwnerId => leadOwnerId === ownerId));
      PROJECTS_LIST_KEYS.forEach(key =>
        this.removeCachedFilter(key, ACCOUNT_MANAGER_FILTERS.projectOwner, projectOwnerId => projectOwnerId === ownerId));
    }

    this.removeSelection();
  }

  /**
   * Clears the selected Account Manager and the cached filters of every list, even when no Account Manager is selected
   */
  clearAll() {
    [...AM_FILTERED_LIST_KEYS, ...LEADS_LIST_KEYS, ...PROJECTS_LIST_KEYS]
      .forEach(key => sessionStorage.removeItem(StorageValues.CachedFilters + key));
    this.removeSelection();
  }

  private removeSelection() {
    sessionStorage.removeItem(StorageValues.SelectedAMId);
    sessionStorage.removeItem(StorageValues.SelectedAMName);
    sessionStorage.removeItem(StorageValues.SelectedAMOwnerId);
    this.selectedSubject.next(null);
  }

  private resetCachedFilters(accountManager: SelectedAccountManager, accountManagerId: string) {
    AM_FILTERED_LIST_KEYS.forEach(key => {
      const cachedFilters = this.readCachedFilters(key);
      const filters = cachedFilters
        ? { ...cachedFilters, accountManager, displayedSearchValue: this.replaceDisplayedAccountManager(cachedFilters, accountManager) }
        : { ...this.getDefaultFilters(key), accountManager, displayedSearchValue: accountManager.name };
      this.cacheFilters(key, filters);
    });

    const ownerDisplayedSearchValue = accountManager.name + DISPLAYED_SEARCH_VALUE_SEPARATOR;
    LEADS_LIST_KEYS.forEach(key =>
      this.cacheFilters(key, { leadOwnerId: accountManagerId, displayedSearchValue: ownerDisplayedSearchValue }));
    PROJECTS_LIST_KEYS.forEach(key =>
      this.cacheFilters(key, { projectOwnerId: accountManagerId, displayedSearchValue: ownerDisplayedSearchValue }));
  }

  private replaceDisplayedAccountManager(cachedFilters: Record<string, unknown>, accountManager: SelectedAccountManager) {
    const displayedSearchValue = cachedFilters.displayedSearchValue as string;
    const segments = displayedSearchValue ? displayedSearchValue.split(DISPLAYED_SEARCH_VALUE_SEPARATOR) : [];
    const previousName = this.getAccountManagerName(cachedFilters[ACCOUNT_MANAGER_FILTERS.accountManager]);

    if (previousName && segments[0] === previousName) {
      segments[0] = accountManager.name;
    } else {
      segments.unshift(accountManager.name);
    }
    return segments.join(DISPLAYED_SEARCH_VALUE_SEPARATOR);
  }

  private getAccountManagerName(accountManager: unknown) {
    if (typeof accountManager !== 'string') {
      return (accountManager as SelectedAccountManager)?.name;
    }
    return accountManager === this.selected?.employeeId ? this.selected.name : undefined;
  }

  private removeDisplayedAccountManager(displayedSearchValue: string) {
    const segments = displayedSearchValue ? displayedSearchValue.split(DISPLAYED_SEARCH_VALUE_SEPARATOR) : [];
    const nameIndex = segments.indexOf(this.selected?.name);
    if (nameIndex !== -1) {
      segments.splice(nameIndex, 1);
    }
    return segments.join(DISPLAYED_SEARCH_VALUE_SEPARATOR);
  }

  private removeCachedFilter(key: StorageValues, filter: AccountManagerFilter, isSelected: (value: unknown) => boolean) {
    const cachedFilters = this.readCachedFilters(key);
    if (!cachedFilters || !isSelected(cachedFilters[filter])) {
      return;
    }

    const remainingFilters = { ...cachedFilters, displayedSearchValue: '' };
    delete remainingFilters[filter];

    if (this.hasActiveFilters(key, remainingFilters)) {
      const displayedSearchValue = this.removeDisplayedAccountManager(cachedFilters.displayedSearchValue as string);
      this.cacheFilters(key, { ...remainingFilters, displayedSearchValue });
    } else {
      sessionStorage.removeItem(StorageValues.CachedFilters + key);
    }
  }

  private hasActiveFilters(key: StorageValues, filters: Record<string, unknown>) {
    const inactiveFilters = this.getInactiveFilters(key);
    return Object.entries(filters).some(([name, value]) => !this.isEmptyFilter(value) && value !== inactiveFilters[name]);
  }

  private getInactiveFilters(key: StorageValues): Record<string, unknown> {
    if (key === StorageValues.AccountsKey) {
      return INACTIVE_ACCOUNTS_FILTERS;
    }
    return AM_FILTERED_LIST_KEYS.includes(key) ? INACTIVE_TRANSACTION_FILTERS : {};
  }

  private isEmptyFilter(value: unknown) {
    return value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length);
  }

  /**
   * Cached account manager filters hold either the account manager or only its employeeId
   */
  private getEmployeeId(accountManager: unknown) {
    return typeof accountManager === 'string' ? accountManager : (accountManager as SelectedAccountManager)?.employeeId;
  }

  private getDefaultFilters(key: StorageValues): Record<string, unknown> {
    return key === StorageValues.AccountsKey ? DEFAULT_ACCOUNTS_FILTERS : {};
  }

  private readCachedFilters(key: StorageValues): Record<string, unknown> | null {
    return JSON.parse(sessionStorage.getItem(StorageValues.CachedFilters + key));
  }

  private cacheFilters(key: StorageValues, filters: object) {
    sessionStorage.setItem(StorageValues.CachedFilters + key, JSON.stringify(filters));
  }

  private readFromStorage(): SelectedAccountManager | null {
    const employeeId = sessionStorage.getItem(StorageValues.SelectedAMId);
    return employeeId ? { employeeId, name: sessionStorage.getItem(StorageValues.SelectedAMName) ?? '' } : null;
  }
}
